import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";

import Langs from "../../app/lang/langs";
import {
    VLEARN_TYPE_META, VLearnEmptyState, VLearnTypeIcon,
    formatVLearnDuration, vlearnSurface, vlearnType
} from "./vlearnUi";
import YouTubeLesson from "./lessons/YouTubeLesson";
import VideoUploadLesson from "./lessons/VideoUploadLesson";
import PdfLesson from "./lessons/PdfLesson";
import SlidesLesson from "./lessons/SlidesLesson";
import DocumentLesson from "./lessons/DocumentLesson";
import TextLesson from "./lessons/TextLesson";
import LinkLesson from "./lessons/LinkLesson";

/**
 * V-Learning — the lesson pane.
 *
 * Two jobs, kept in one place. It draws the chrome every lesson shares — the type, the title,
 * the length, the author's introduction — so that the seven content components below only
 * ever have to render their own medium. And it dispatches on `lesson.type`.
 *
 * An unrecognised type renders an explanatory panel rather than throwing. Courses outlive the
 * client that authored them: a lesson type added to the backend and to the authoring UI will
 * reach a learner whose tab has been open since before the deploy, and the correct behaviour
 * for that learner is one lesson they cannot open — not a blank application.
 */

const LESSON_COMPONENTS = {
    video_youtube: YouTubeLesson,
    video_upload: VideoUploadLesson,
    pdf: PdfLesson,
    slides: SlidesLesson,
    document: DocumentLesson,
    text: TextLesson,
    link: LinkLesson
}

const focusRing = {
    '&:focus-visible': {
        outline: '2px solid var(--orange, rgb(255,144,21))',
        outlineOffset: '2px'
    }
}

export default function LessonRenderer({ lesson, course, onProgress, onOpenQuiz }) {

    if (!lesson) return null

    const typeMeta = VLEARN_TYPE_META[lesson.type] || null
    const duration = formatVLearnDuration(lesson.duration_seconds)

    return (
        <Box component="article" aria-labelledby={`vlearn-lesson-${lesson.id}`}>

            <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <VLearnTypeIcon type={lesson.type} />
                    {typeMeta ? (
                        <Typography sx={{
                            ...vlearnType.meta, fontWeight: 600,
                            textTransform: 'uppercase', letterSpacing: '.05em'
                        }}>
                            <Langs str={typeMeta.key} />
                        </Typography>
                    ) : null}
                    {duration ? (
                        <Typography sx={{ ...vlearnType.meta }}>{`· ${duration}`}</Typography>
                    ) : null}
                    {lesson.is_mandatory ? (
                        <Chip
                            size="small"
                            label={<Langs str="VLEARN_REQUIRED" />}
                            sx={{
                                height: 20, fontSize: '0.68rem', fontWeight: 600, borderRadius: '6px',
                                backgroundColor: 'rgba(251, 105, 98, 0.14)', color: '#B3271F'
                            }}
                        />
                    ) : null}
                </Box>

                <Typography
                    component="h2"
                    id={`vlearn-lesson-${lesson.id}`}
                    sx={{ ...vlearnType.pageTitle, fontSize: '1.25rem' }}
                >
                    {lesson.title}
                </Typography>

                {/* The author's introduction, as text. Same rule as `TextLesson`: an
                    author-typed field is never interpreted as markup. */}
                {lesson.description ? (
                    <Typography sx={{
                        ...vlearnType.body, mt: 1, whiteSpace: 'pre-wrap', maxWidth: '72ch'
                    }}>
                        {lesson.description}
                    </Typography>
                ) : null}
            </Box>

            {renderContent()}
        </Box>
    )

    function renderContent() {
        // A quiz lesson has no medium to render: it is a doorway into the attempt screen,
        // which owns the timer and the submission and lives outside this pane.
        if (lesson.type === 'quiz') {
            return (
                <Box sx={{ ...vlearnSurface.panel, maxWidth: 560 }}>
                    <Typography sx={{ ...vlearnType.sectionTitle }}>
                        {lesson.quiz_title || <Langs str="VLEARN_QUIZ" />}
                    </Typography>
                    <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {lesson.quiz_passing_percent ? (
                            <Typography sx={{ ...vlearnType.body }}>
                                <Langs str="VLEARN_QUIZ_PASSING" params={{ percent: lesson.quiz_passing_percent }} />
                            </Typography>
                        ) : null}
                        {lesson.quiz_max_attempts ? (
                            <Typography sx={{ ...vlearnType.body }}>
                                <Langs
                                    str={Number(lesson.quiz_max_attempts) === 1
                                        ? 'VLEARN_QUIZ_ATTEMPTS_ONE' : 'VLEARN_QUIZ_ATTEMPTS_MANY'}
                                    params={{ count: lesson.quiz_max_attempts }}
                                />
                            </Typography>
                        ) : null}
                        {lesson.quiz_time_limit ? (
                            <Typography sx={{ ...vlearnType.body }}>
                                <Langs str="VLEARN_QUIZ_TIME_LIMIT" params={{ minutes: lesson.quiz_time_limit }} />
                            </Typography>
                        ) : null}
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={!lesson.id_quiz}
                        onClick={() => onOpenQuiz && onOpenQuiz(lesson.id_quiz, lesson.id)}
                        sx={{
                            ...focusRing, mt: 2, textTransform: 'none', fontSize: '0.82rem',
                            backgroundColor: 'var(--orange, rgb(255,144,21))',
                            '&:hover': { backgroundColor: '#e07f0d' }
                        }}
                    >
                        <Langs str="VLEARN_START_QUIZ" />
                    </Button>
                </Box>
            )
        }

        const Component = LESSON_COMPONENTS[lesson.type]
        if (!Component) {
            return (
                <Box sx={{ ...vlearnSurface.card }}>
                    <VLearnEmptyState
                        icon={<ReportGmailerrorredIcon />}
                        title={<Langs str="VLEARN_UNKNOWN_TYPE_TITLE" />}
                        description={<Langs str="VLEARN_UNKNOWN_TYPE_DESC" />}
                    />
                </Box>
            )
        }

        return (
            <Component
            /*
             * `key={lesson.id}` FORCES A REMOUNT when the lesson changes.
             *
             * Without it, moving between two lessons of the same type reuses the component
             * instance — and VideoUploadLesson keeps its accumulated seconds and last playhead
             * in refs that are only flushed on unmount. So 3 seconds watched on lesson A (still
             * under the 5s reporting threshold) were credited to lesson B, along with A's
             * resume position. A lost the time it earned and B gained time nobody spent.
             */
            key={lesson.id}
                lesson={lesson}
                course={course}
                onProgress={onProgress}
                onOpenQuiz={onOpenQuiz}
            />
        )
    }
}
